<template>
  <div class="activity-stage">
    <div class="mrs-news-syllables-container">
      <div class="left">
        <div class="type-syllables">
          <span>{{ currentRound.instructions | capitalize }}</span>
        </div>
        <div class="chalkboard-text-container">
          <span v-text="fullWordDisplay"></span>
        </div>
        <img :src="chalkBoardTeacherImage" alt="" />

        <div :class="{ 'selection-container': true, isMobile }" v-show="syllablesLeft === 0">
          <div class="definitions-container">
            <div
              :class="{ 'format-instructions-text': true, isMobile }"
              v-for="(action, idx) in definitionActions"
              :key="idx"
              :track-by="idx"
            >
              <span v-text="`${idx + 1}. `"></span>
              <span :class="action.class">{{ action.instruction | capitalize }}&nbsp;</span>
              <span v-text="action.text"></span>
            </div>
          </div>

          <div class="actions-container">
            <TextFormattingButtons :options="optionsTextFormattingButtons" />
          </div>

          <div class="text-to-format-container">
            <div
              v-for="(word, idx) in definitionActions"
              :class="{ 'text-to-format': true, isMobile }"
              :id="word.id"
              :track-by="idx"
              :key="idx"
            >
              <span
                tabindex="-1"
                @mousedown="enableSelection"
                @mouseleave="saveSelection"
                @blur="disableSelection"
                v-text="word.text"
              >
              </span>
            </div>
          </div>

          <div class="facts-stage">
            <div
              v-for="(factStep, idx) in factsSteps"
              :class="factStep.className"
              :key="idx"
              :track-by="idx"
            >
              <blinking-cursor-text :text-step="factStep.stepIndex" :text="factStep.answer">
              </blinking-cursor-text>
            </div>
          </div>
        </div>
      </div>

      <div class="right">
        <div v-for="(syllable, idx) in syllables" :key="idx" :track-by="idx">
          <blinking-cursor-text :text-step="syllable.stepIndex" :text="syllable.answer">
          </blinking-cursor-text>
        </div>
      </div>

      <div id="chalkboard"></div>
    </div>

    <slot name="keyboard"></slot>
  </div>
</template>

<script>
import { TweenMax } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import ActivityManager from 'mixins/ActivityManager';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ErrorLogger from 'mixins/ErrorLogger';
import { createNamespacedHelpers } from 'vuex';

import chalkBoardTeacherImage from 'assets/images/activities/mrs-news-syllables/chalkboard-teacher.jpg';

import TextFormattingButtons from 'components/buttons/TextFormattingButtons.vue';

import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';

const application = createNamespacedHelpers('application');

const getDefinitionActions = definitionText => {
  const { bold, italic, underline } = definitionText;

  const actions = {
    bold: bold && {
      isSolved: false,
      text: bold.trim(),
      instruction: 'bold',
      class: 'bold-formatting',
      id: 'bold-action'
    },
    italic: italic && {
      isSolved: false,
      text: italic.trim(),
      instruction: 'italicize',
      class: 'italic-formatting',
      id: 'italic-action'
    },
    underline: underline && {
      isSolved: false,
      text: underline.trim(),
      instruction: 'underline',
      class: 'underline-formatting',
      id: 'underline-action'
    }
  };

  return Object.keys(actions)
    .map(prop => actions[prop])
    .filter(q => q);
};

export default {
  mixins: [AssetLoader, ActivityManager, ErrorLogger, TypingActivityHelper],

  components: {
    BlinkingCursorText,
    TextFormattingButtons
  },

  computed: {
    ...application.mapState({
      isMobile: state => state.device.isMobile
    }),
    optionsTextFormattingButtons() {
      if (!this.currentStep) {
        return null;
      }
      return {
        boldCallback: () => this.handleAction('bold'),
        italicCallback: () => this.handleAction('italicize'),
        underlineCallback: () => this.handleAction('underline')
      };
    }
  },

  data() {
    return {
      voice: null,
      sounds: null,
      solvedAll: false,
      fullWordDisplay: null,
      activityName: 'MrsNewsSyllables',
      actionsLeft: null,
      syllables: [],
      syllablesLeft: null,
      definitionText: '',
      definitionActions: [],
      textWords: [],
      factsIndex: 0,
      chalkBoardTeacherImage
    };
  },
  methods: {
    async loadActivity() {
      const activityState = this.getActivityState();
      this.createRounds(activityState);
      this.sounds = await this.loadSounds();
    },

    solveAllFormattings() {
      if (this.solvedAll) return;
      this.solvedAll = true;
      this.sndBell();
      ['bold', 'italicize', 'underline'].forEach(action => this.handleAction(action, true));
    },

    handleAction(action, solveAll = false) {
      const defAction = this.definitionActions.find(def => {
        if (def.isSolved) return false;
        return solveAll
          ? def.instruction === action
          : def.instruction === action && def.text === this.textSelection;
      });

      if (!defAction) {
        this.textSelection = '';
        return;
      }

      if (!solveAll) {
        this.sndBell();
        defAction.isSolved = true;
      }

      // append formatting class (e.g. bold, italic)
      document.getElementById(defAction.id).classList.add(defAction.class);
      this.actionsLeft -= 1;
      if (!this.actionsLeft) {
        const elemToAnimate =
          document.querySelector('.facts-definition') || document.querySelector('.facts-sentence');

        if (elemToAnimate) {
          this.sounds[1].play();
          this.showKeyboard({ enable: true });
          TweenMax.to(elemToAnimate, 0.5, { opacity: 1 });
        } else {
          this.handleRoundEnd();
        }
      }
    },

    enableSelection(event) {
      const ev = event;
      if (ev.target.className.indexOf('selectable') === -1) {
        ev.target.className = 'selectable';
      }
    },

    disableSelection(event) {
      const ev = event;
      ev.target.className = '';
      window.getSelection().empty();
    },

    saveSelection() {
      this.textSelection = window
        .getSelection()
        .toString()
        .trim();
    },

    getActivityState() {
      const { rounds } = this.activityData;
      const { definitionText, syllableSteps, factsSteps } = rounds[0];

      this.definitionActions = getDefinitionActions(definitionText);
      this.syllablesLeft = syllableSteps.length;
      this.actionsLeft = this.definitionActions.length;
      this.factsIndex = syllableSteps.length;
      this.factsSteps = factsSteps;
      this.syllables = syllableSteps;
      this.voice = this.getRandomVoice();

      return {
        rounds,
        instructions: {
          voice: `${this.voiceInstructionsPrefix}/mrsnewssyllables-${this.voice}-1.mp3`
        }
      };
    },

    async loadSounds() {
      const boldAudio = `${this.voiceInstructionsPrefix}/mrsnewssyllables-${this.voice}-2.mp3`;
      const definitionAudio = `${this.voiceInstructionsPrefix}/mrsnewssyllables-${this.voice}-3.mp3`;
      const sounds = [boldAudio, definitionAudio];

      this.queueAudio(sounds);
      const cache = await this.downloadAudio();
      return sounds.map(sound => cache.get(sound));
    },

    startActivity() {
      this.setupNewRound();
      this.enableKeyboard();
      this.errorLoggerStart();
    },

    handleCorrectKey() {
      const { className, answer } = this.currentStep;

      this.sndType();
      this.incrementTypingIndex();

      if (this.isEndOfCurrentSentence) {
        this.sndBell();
        this.completeStep();
        this.setCurrentStep();

        if (this.syllablesLeft) {
          this.fullWordDisplay = answer;
          this.syllablesLeft -= 1;
          if (!this.syllablesLeft) {
            this.hideKeyboard();

            this.sounds[0].on('end', () => {
              // auto format words if on tablet
              if (this.isMobile) this.solveAllFormattings();
            });
            this.sounds[0].play();
          }
        } else if (className === 'facts-definition') {
          this.handleFactDefinitionStep();
        } else if (className === 'facts-sentence') {
          this.handleRoundEnd();
        }
      }
    },

    handleFactDefinitionStep() {
      const sentence = document.querySelector('.facts-sentence');

      if (sentence) {
        TweenMax.to(sentence, 0.5, { opacity: 1 });
      } else {
        this.handleRoundEnd();
      }
    },

    handleRoundEnd() {
      TweenMax.delayedCall(1, this.playCongrats);
      TweenMax.delayedCall(1.5, this.hideKeyboard);
      TweenMax.delayedCall(2, () => this.completeActivity(false));
    }
  }
};
</script>

<style lang="scss" scoped>
.mrs-news-syllables-container {
  overflow: hidden;
}

.left,
.right {
  display: inline-block;
  font-family: hwtfutura;
  user-select: none;
}

.facts-definition,
.facts-sentence {
  opacity: 0;
  font-size: 18px;
  letter-spacing: 2px;
}

.selectable {
  user-select: text;
  -moz-user-select: text;
  -webkit-user-select: text;
  &::selection {
    background-color: #000;
    color: #fff;
  }
}

.left {
  position: relative;
  top: -40px;
  width: 675px;
}

.solve-all-button {
  background-color: black;
  transform: translateY(-60%);
  border: none;
  border-radius: 5px;
  padding: 5px 15px;
  font-size: 1rem;
  display: inline-block;
  font-family: hwtfutura;
  color: white;
  user-select: none;
}

.type-syllables {
  position: absolute;
  left: 150px;
  top: 60px;
  width: 80px;
  font-size: 20px;
}

.definitions-container,
.actions-container {
  width: 500px;
}

.format-instructions-text {
  display: inline-block;
  margin-right: 10px;
  margin-bottom: 15px;
}

.format-instructions-text.isMobile {
  margin-bottom: 5px;
}

.actions-container {
  .text-format-button {
    display: inline-block;
    cursor: pointer;
    margin-right: 20px;
  }
}

.chalkboard-text-container {
  position: absolute;
  left: 250px;
  top: 140px;
  font-size: 35px;
}

.selection-container {
  position: absolute;
  width: 750px;
  left: 195px;
  top: 245px;

  .bold-formatting {
    font-family: 'hwtfutura-bold';
  }
  .italic-formatting {
    font-style: italic;
  }
  .underline-formatting {
    text-decoration: underline;
  }
}

.selection-container.isMobile {
  top: 230px !important;
}

.text-to-format-container {
  .text-to-format {
    margin-right: 30px;
    font-size: 35px;
    display: inline-block;
  }

  .text-to-format.isMobile {
    font-size: 25px !important;
  }
}

.right {
  vertical-align: top;
  font-size: 30px;
  padding: 30px 0 0 20px;
}

.tablet .selection-container {
  transform: scale(0.85);
  left: 150px;
}
</style>
